import { ActionDatapoint, returnTopExecutingActionDatapoint } from 'owa-analytics';
import {
    Calendar,
    addToCalendarsCache,
    removeCalendarFromCalendarsCache,
} from 'owa-calendar-cache';
import { CalendarGroup, getCalendars } from 'owa-calendar-surface-calendars-store';
import { CreateCalendarResult, createCalendarService } from '../services/createCalendarService';
import { MailboxInfo, getUserMailboxInfo } from 'owa-client-ids';
import { addCalendarAtIndex, pushCalendar, spliceCalendar } from './modifyCalendarState';
import {
    calendarNotificationsBarCreateCalendar,
    calendarNotificationsBarCreateCalendarFailed,
} from './createCalendar.locstring.json';
import loc, { format } from 'owa-localize';

import { ControlIcons } from 'owa-control-icons';
import { CreateCalendarMutationVariables } from '../graphql/__generated__/createCalendarMutation.interface';
import { LocalCalendarEntry } from 'owa-graph-schema';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyShowCalendarNotification } from 'owa-calendar-notification-bar';
import showCalendarErrorNotification from './showCalendarErrorNotification';
import { trace } from 'owa-trace';
import { tryAgainLabel } from 'owa-locstrings/lib/strings/tryagainlabel.locstring.json';

/**
 * Action to create a calendar
 * @param {string} newCalendarName - name of new calendar
 * @param {CalendarGroup} group - parent calendar group
 */
export let createCalendar = createLegacyActionWithDatapoint('CreateCalendar', {
    customData: (calendarName: string, group: CalendarGroup, userIdentity: string) => {
        return { rejected: !calendarName || calendarName.trim().length == 0 };
    },
})(async function createCalendarInternal(
    calendarName: string,
    group: CalendarGroup,
    userIdentity: string
): Promise<Calendar> {
    if (!calendarName || calendarName.trim().length == 0) {
        return null;
    }

    const dataPoint = returnTopExecutingActionDatapoint();
    const mailboxInfo = getUserMailboxInfo(userIdentity);

    let instantCreatedCalendar = instantCreateCalendar(calendarName, group, mailboxInfo);

    const returnCalendar = await executeCreateCalendarRequest(
        instantCreatedCalendar,
        mailboxInfo,
        dataPoint,
        calendarName,
        group
    );

    if (returnCalendar == null) {
        onRequestFailed(userIdentity, instantCreatedCalendar);

        showCalendarErrorNotification(
            'createCalendar',
            format(loc(calendarNotificationsBarCreateCalendarFailed), calendarName),
            ControlIcons.Error,
            loc(tryAgainLabel),
            () => createCalendar(calendarName, group, userIdentity)
        );
    }

    return returnCalendar;
});

async function executeCreateCalendarRequest(
    calendar: Calendar,
    mailboxInfo: MailboxInfo,
    dataPoint: ActionDatapoint,
    calendarName: string,
    group: CalendarGroup
) {
    if (isFeatureEnabled('cal-calendarPickerReorder')) {
        addCalendarAtIndex(getCalendars(), calendar);
    } else {
        pushCalendar(getCalendars(), calendar);
    }

    if (dataPoint) {
        dataPoint.markUserPerceivedTime(); // mark user perceived time here as soon as we dispatch the request to create
    }
    lazyShowCalendarNotification.import().then(showCalendarNotification => {
        showCalendarNotification(
            'createCalendar',
            'Surface',
            format(loc(calendarNotificationsBarCreateCalendar), calendarName),
            { icon: ControlIcons.CheckMark }
        );
    });

    try {
        const mutationInput: CreateCalendarMutationVariables = {
            newCalendarName: calendar.displayName,
            parentGroupId: group.groupId,
            emailAddress: null,
            mailboxInfo: mailboxInfo,
        };

        const mutationResult = await createCalendarService(mutationInput);
        return processServiceResponse(mutationResult, calendar, group);
    } catch (error) {
        return null;
    }
}

function onRequestFailed(userIdentity: string, calendar: Calendar) {
    let calendarIndex = findInArray(getCalendars(), item => {
        return item.id.id == calendar.id.id && item.id.mailboxInfo.userIdentity == userIdentity;
    });

    if (calendarIndex && calendarIndex.index >= 0) {
        spliceCalendar(getCalendars(), calendarIndex.index);
    } else {
        trace.warn(`[renameCalendar] Cannot find calendar id: ${calendar.id}`);
    }

    removeCalendarFromCalendarsCache(calendar);
}

function instantCreateCalendar(
    calendarName: string,
    group: CalendarGroup,
    mailboxInfo: MailboxInfo
): Calendar {
    let newCalendarId = Math.random().toString();
    let newFolderId = Math.random().toString();
    let calendar: Calendar = {
        id: {
            id: newCalendarId,
            mailboxInfo: mailboxInfo,
        },
        displayName: calendarName,
        colorScheme: null,
    };
    let calendarEntry: LocalCalendarEntry = {
        calendarId: {
            itemId: { Id: newCalendarId },
            mailboxInfo: mailboxInfo,
        },
        ParentGroupId: group.groupId,
        CalendarName: calendarName,
        FolderId: { Id: newFolderId },
        CanShare: false,
        IsReadOnly: true,
        EffectiveRights: { CreateContents: false },
    };
    addToCalendarsCache(calendarEntry);
    return calendar;
}

function processServiceResponse(
    mutationResult: CreateCalendarResult,
    calendar: Calendar,
    group: CalendarGroup
): Calendar {
    removeCalendarFromCalendarsCache(calendar);

    if (mutationResult) {
        let calendarEntry: LocalCalendarEntry = {
            calendarId: {
                itemId: mutationResult.newCalendarEntryItemId,
                mailboxInfo: calendar.id.mailboxInfo,
            },
            ParentGroupId: group.groupId,
            CalendarName: calendar.displayName,
            FolderId: mutationResult.newFolderId,
            CanShare: true,
            IsReadOnly: false,
            EffectiveRights: { CreateContents: true },
        };

        addToCalendarsCache(calendarEntry);
        calendar.id.id = mutationResult.newCalendarEntryItemId.Id;
        let colorScheme = getCalendarColorScheme(calendar.id);
        calendar.colorScheme = colorScheme;

        trace.info(`[createCalendar] Created a new calendar on server: ${calendar.displayName}`);
        return calendar;
    } else {
        return null;
    }
}

export default createCalendar;
